import * as Dialog from "@radix-ui/react-dialog";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, CircleHelp, ExternalLink, FileText, PackageOpen, RotateCcw, Search, Undo2, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { ReturnsAnalyticsPanel } from "./ReturnsAnalytics";
import { ModuleRequired, useModuleFeature } from "@/components/ModuleGate";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton, Tip } from "@/components/ui";
import { toast } from "sonner";
import { api, ApiError, canManage, type ReturnCaseItem, type ReturnClarification, type ReturnStatus, type ShopifyReturnState } from "@/lib/api";
import { countryName, fmtDateTime, fmtNumber, fmtRelative } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";
/** Zustand der Ware nach Prüfung – immer mit Logiful-Klassifizierung A/B/C */
export const CONDITION_LABEL: Record<string, string> = { A: "A · Wie neu", B: "B · Verpackung beschädigt", C: "C · Defekt" };
const CONDITION_TONE: Record<string, "success" | "warning" | "danger"> = { A: "success", B: "warning", C: "danger" };
const STATUS_TONE: Record<ReturnStatus, "neutral" | "success" | "warning" | "danger"> = { checked: "neutral", notified: "neutral", shop_synced: "success", shop_sync_failed: "danger" };

export function ConditionBadges({ conditions }: { conditions: Record<string, number> }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {Object.entries(conditions).sort().map(([c, n]) => (
        <Badge key={c} tone={CONDITION_TONE[c] ?? "neutral"}>{n}× {CONDITION_LABEL[c] ?? c}</Badge>
      ))}
    </span>
  );
}

const customerName = (r: Pick<ReturnCaseItem, "customer">) => {
  const c = r.customer;
  return c ? [c.firstName, c.lastName].filter(Boolean).join(" ") || c.company || "—" : "—";
};

const STATUSES: { key: ReturnStatus | ""; label: string }[] = [
  { key: "", label: msg("Alle") },
  { key: "checked", label: msg("Geprüft") },
  { key: "notified", label: msg("Gemeldet") },
  { key: "shop_synced", label: msg("Im Shop") },
  { key: "shop_sync_failed", label: msg("Shop-Fehler") },
];

/**
 * Login-Sprung ins Retourenportal: eingerichtet → Verwaltung öffnen, sonst einrichten.
 * Neues Fenster wird sofort geöffnet (sonst blockiert der Browser), die Adresse folgt nach der Signatur.
 */
export function ReturnsPortalButton({ q, variant = "default" }: { q?: string; variant?: "default" | "link" }) {
  const { tenantId, role } = useTenant();
  const info = useQuery({ queryKey: ["returns-portal", tenantId], queryFn: () => api.returns.portal(tenantId), staleTime: 5 * 60_000, retry: false });
  const [busy, setBusy] = useState(false);
  if (!info.data?.configured || !canManage(role)) return null;
  const exists = !!info.data.status;
  const open = async () => {
    const win = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const { url } = await api.returns.openPortal(tenantId, q ? { q } : {});
      if (win) win.location.href = url;
      else window.location.href = url;
    } catch (e) {
      win?.close();
      toast.error("Retourenportal nicht erreichbar", { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "" });
    } finally {
      setBusy(false);
    }
  };
  if (variant === "link")
    return exists ? <button onClick={() => void open()} disabled={busy} className="inline-flex items-center gap-1 underline underline-offset-2 disabled:opacity-50">{t("Im Retourenportal")} <ExternalLink className="size-3" /></button> : null;
  return (
    <Tip content={exists ? (info.data.status!.setupDone ? t("Verwaltung des Retourenportals öffnen (Ablauf, Regeln, Branding, Retouren)") : t("Retourenportal öffnen – Logiful verbindet noch Shop und Versand")) : t("Euer eigenes Retourenportal für Endkunden einrichten")}>
      <Button variant={exists ? "secondary" : "primary"} icon={<ExternalLink className="size-4" />} loading={busy} onClick={() => void open()}>
        {exists ? t("Retourenportal öffnen") : t("Retourenportal einrichten")}
      </Button>
    </Tip>
  );
}

/** Anzahl am Filter; 0 gedämpft, damit man vor dem Klick sieht, dass nichts übrig bliebe */
function Count({ n, active }: { n: number | undefined; active: boolean }) {
  if (n == null) return null;
  return <span className={cx("tabular-nums text-[11.5px]", active ? "opacity-80" : n === 0 ? "text-fg-subtle/60" : "text-fg-subtle")}>{fmtNumber(n)}</span>;
}

function FilterChip({ active, n, onClick, children }: { active: boolean; n: number | undefined; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} aria-pressed={active} className={cx("h-8 px-2.5 rounded-lg border text-[12.5px] font-medium inline-flex items-center gap-1.5", active ? "border-ink bg-ink text-ink-fg" : "border-border hover:bg-surface-2", !active && n === 0 && "text-fg-subtle")}>
      {children}
      <Count n={n} active={active} />
    </button>
  );
}

/** Retouren des Kunden – aus der Retouren-Bearbeitung von Logiful übernommen */
export function ReturnsPage() {
  const { tenantId } = useTenant();
  const analyticsOk = useModuleFeature("analytics", "returns_analytics");
  const [params, setParams] = useSearchParams();
  const [input, setInput] = useState(params.get("q") ?? "");
  const status = (params.get("status") as ReturnStatus | null) ?? undefined;
  const undeliverable = params.get("undeliverable") === "1" ? ("1" as const) : undefined;
  const condition = (params.get("condition") as "A" | "B" | "C" | null) ?? undefined;
  const page = Number(params.get("page") ?? 1);
  const open = params.get("open");
  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => input !== (params.get("q") ?? "") && update({ q: input.trim() || null }), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);
  const q = params.get("q") ?? undefined;
  const filtered = !!(status || undeliverable || condition || q);
  const reset = () => {
    setInput("");
    update({ status: null, undeliverable: null, condition: null, q: null });
  };
  const view = params.get("view") === "klaerfaelle" ? "klaerfaelle" : params.get("view") === "auswertung" ? "auswertung" : "retouren";
  const clar = useQuery({ queryKey: ["return-clarifications", tenantId], queryFn: () => api.returns.clarifications(tenantId), staleTime: 60_000 });
  const res = useQuery({ queryKey: ["returns", tenantId, q, status, undeliverable, condition, page], queryFn: () => api.returns.list(tenantId, { q, status, undeliverable, condition, page }), placeholderData: keepPreviousData, refetchInterval: 60_000 });
  const d = res.data;

  return (
    <div className="pb-16">
      <PageHeader title={t("Retouren")} description={t("Rücksendungen eurer Kunden – geprüft im Logiful-Lager, mit Zustand und Rückmeldung an den Shop.")} actions={<ReturnsPortalButton />} />
      <div className="px-4 md:px-8 space-y-4">
        {res.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : !d?.configured && !d?.total ? (
          <Card>
            <EmptyState icon={<RotateCcw className="size-5" />} title={t("Retouren sind noch nicht verbunden")}>
              {t("Sobald Logiful euer Konto mit der Retouren-Bearbeitung verknüpft hat, erscheinen eure Rücksendungen hier – mit Zustand je Artikel und Verknüpfung zum Auftrag.")}
            </EmptyState>
          </Card>
        ) : (
          <>
            {/* Reiter: Retouren | Klärfälle (Sendungen, die das Lager nicht normal verarbeiten konnte) */}
            <div className="flex items-center gap-1 border-b border-border">
              {([["retouren", msg("Retouren"), d?.total], ["klaerfaelle", msg("Klärfälle"), clar.data?.length], ["auswertung", msg("Auswertung"), null]] as const).map(([key, label, n]) => (
                <button key={key} onClick={() => update({ view: key === "retouren" ? null : key, open: null, days: null })} aria-pressed={view === key} className={cx("h-9 px-3 -mb-px border-b-2 text-[13px] font-medium inline-flex items-center gap-1.5", view === key ? "border-fg text-fg" : "border-transparent text-fg-muted hover:text-fg")}>
                  {t(label)}
                  {n != null && <span className={cx("text-[11.5px] tabular-nums", key === "klaerfaelle" && n > 0 ? "text-warning" : "text-fg-subtle")}>{fmtNumber(n)}</span>}
                </button>
              ))}
            </div>
            {view === "klaerfaelle" ? (
              <ClarificationsList items={clar.data} loading={clar.isLoading} />
            ) : view === "auswertung" ? (
              analyticsOk ? <ReturnsAnalyticsPanel /> : <ModuleRequired module="analytics" feature="returns_analytics" />
            ) : (
            <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                [msg("Letzte 7 Tage"), d!.stats.week],
                [msg("Letzte 30 Tage"), d!.stats.month],
                [msg("Rückläufer (30 Tage)"), d!.stats.undeliverable30],
                [msg("Shop-Meldung fehlgeschlagen"), d!.stats.failed],
              ].map(([label, n]) => (
                <Card key={label as string} className="px-4 py-3">
                  <p className="text-[12px] text-fg-muted">{t(label as string)}</p>
                  <p className={cx("text-[20px] font-semibold tabular-nums", label === "Shop-Meldung fehlgeschlagen" && (n as number) > 0 && "text-danger")}>{fmtNumber(n as number)}</p>
                </Card>
              ))}
            </div>
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("Auftragsnummer, Kunde, E-Mail oder SKU suchen …")} leading={<Search className="size-4" />} className="h-11 text-[14px]" />
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex flex-wrap p-0.5 rounded-lg bg-surface-2 border border-border">
                {STATUSES.map((s) => {
                  const active = (status ?? "") === s.key;
                  return (
                    <button key={s.key} onClick={() => update({ status: s.key || null })} aria-pressed={active} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium inline-flex items-center gap-1.5", active ? "bg-surface shadow-sm text-fg" : "text-fg-muted hover:text-fg")}>
                      {t(s.label)}
                      <Count n={d!.facets?.[(s.key || "all") as keyof NonNullable<typeof d.facets>]} active={active} />
                    </button>
                  );
                })}
              </div>
              {(["A", "B", "C"] as const).map((c) => (
                <FilterChip key={c} active={condition === c} n={d!.facets?.[c]} onClick={() => update({ condition: condition === c ? null : c })}>{CONDITION_LABEL[c]}</FilterChip>
              ))}
              <FilterChip active={!!undeliverable} n={d!.facets?.undeliverable} onClick={() => update({ undeliverable: undeliverable ? null : "1" })}><Undo2 className="size-3.5" /> {t("Nur Rückläufer")}</FilterChip>
              {filtered && <button onClick={reset} className="h-8 px-2 text-[12.5px] text-fg-muted hover:text-fg underline underline-offset-2">{t("Filter zurücksetzen")}</button>}
            </div>
            <Card className="overflow-hidden">
              {d!.items.length === 0 ? (
                <EmptyState icon={<PackageOpen className="size-5" />} title={t("Keine Retouren für diese Auswahl")} action={filtered ? <Button onClick={reset}>{t("Filter zurücksetzen")}</Button> : undefined}>
                  {filtered ? t("Die gewählten Filter gelten gleichzeitig – die Zahl am Filter zeigt, wie viele Retouren übrig bleiben.") : undefined}
                </EmptyState>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="border-b border-border text-left text-[12px] text-fg-muted">
                        {[msg("Auftrag"), msg("Art"), msg("Kunde"), msg("Zustand"), msg("Status"), "Shopify", msg("Geprüft")].map((h) => <th key={h} className="px-4 py-2.5 font-medium whitespace-nowrap">{t(h)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {d!.items.map((r) => (
                        <tr key={r.id} onClick={() => update({ open: r.id })} className="border-b border-border last:border-0 hover:bg-surface-2/50 cursor-pointer">
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <span className="font-medium tabular-nums">{r.shopOrderNumber ?? r.warehouseOrderNumber ?? "—"}</span>
                            <span className="block text-[11.5px] text-fg-subtle">{r.units} {t("Artikel")}</span>
                          </td>
                          {/* Rückläufer = Sendung kam unzugestellt zurück; Retoure = vom Endkunden zurückgeschickt */}
                          <td className="px-4 py-2.5 whitespace-nowrap">{r.undeliverable ? <Badge tone="warning">{t("Rückläufer")}</Badge> : <span className="text-fg-muted">{t("Retoure")}</span>}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap">{customerName(r)}<span className="block text-[11.5px] text-fg-subtle">{[r.customer?.zip, r.customer?.city].filter(Boolean).join(" ")}</span></td>
                          <td className="px-4 py-2.5"><ConditionBadges conditions={r.conditions} /></td>
                          <td className="px-4 py-2.5 whitespace-nowrap"><Badge tone={STATUS_TONE[r.status]}>{t(r.statusLabel)}</Badge></td>
                          <td className="px-4 py-2.5 whitespace-nowrap">{r.shopify ? <Badge tone={SHOPIFY_TONE[r.shopify.state]}>{SHOPIFY_SHORT[r.shopify.state]}</Badge> : <span className="text-fg-subtle">—</span>}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-fg-muted" title={r.processedAt ? fmtDateTime(r.processedAt) : undefined}>{fmtRelative(r.processedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            {d!.total > d!.pageSize && (
              <div className="flex items-center justify-between text-[12.5px] text-fg-muted">
                <span className="tabular-nums">{fmtNumber((d!.page - 1) * d!.pageSize + 1)}–{fmtNumber(Math.min(d!.total, d!.page * d!.pageSize))} {t("von")} {fmtNumber(d!.total)}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" icon={<ChevronLeft className="size-4" />} disabled={d!.page <= 1} onClick={() => update({ page: String(d!.page - 1) })}>{t("Zurück")}</Button>
                  <Button size="sm" variant="ghost" disabled={d!.page * d!.pageSize >= d!.total} onClick={() => update({ page: String(d!.page + 1) })}>{t("Weiter")} <ChevronRight className="size-4" /></Button>
                </div>
              </div>
            )}
            </>
            )}
          </>
        )}
      </div>
      {open && <ReturnSheet id={open} onClose={() => update({ open: null })} />}
    </div>
  );
}

/** Klärfälle: Grund, Sendung, Notiz des Lagers – verknüpft mit dem Auftrag, sofern bekannt */
export function ClarificationsList({ items, loading, showUnassignedHint }: { items: ReturnClarification[] | undefined; loading: boolean; showUnassignedHint?: boolean }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!items?.length)
    return (
      <Card>
        <EmptyState icon={<CircleHelp className="size-5" />} title={t("Keine Klärfälle")}>
          {t("Klärfälle entstehen, wenn eine Rücksendung nicht normal verarbeitet werden kann – z. B. fremde Ware, leeres Paket oder unbekannter Auftrag. Sie erscheinen hier mit Grund und Hinweis des Lagers.")}
        </EmptyState>
      </Card>
    );
  return (
    <Card className="overflow-hidden">
      {showUnassignedHint && <p className="px-4 py-2.5 border-b border-border text-[12.5px] text-fg-muted">{t("Keinem Kunden zuordenbar – Auftrag fehlt oder ist unbekannt.")}</p>}
      <ul className="divide-y divide-border">
        {items.map((c) => (
          <li key={c.id} className="px-4 py-3 text-[13px] grid gap-x-6 gap-y-1.5 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.4fr)]">
            <div className="min-w-0">
              <div className="flex flex-wrap gap-1.5">{c.reasons.length ? c.reasons.map((r) => <Badge key={r} tone="warning">{r}</Badge>) : <Badge>{t("Klärfall")}</Badge>}</div>
              <p className="mt-1 text-[11.5px] text-fg-subtle tabular-nums">{c.reportedAt ? fmtDateTime(c.reportedAt) : "—"}{c.location ? t(" · Standort {location}", { location: c.location }) : ""}</p>
            </div>
            <div className="min-w-0 text-[12.5px]">
              {c.orderId && c.orderNumber ? (
                <Link to={`/orders?open=${c.orderId}`} className="font-medium underline underline-offset-2">{t("Auftrag")} {c.orderNumber}</Link>
              ) : (
                <span className="font-medium">{c.shopOrderNumber ? t("Auftrag {shopOrderNumber}", { shopOrderNumber: c.shopOrderNumber }) : c.warehouseOrderNumber ? t("Lager-Auftrag {warehouseOrderNumber}", { warehouseOrderNumber: c.warehouseOrderNumber }) : t("Auftrag unbekannt")}</span>
              )}
              {c.trackingNumber && <span className="block font-mono text-[11.5px] text-fg-muted truncate">{t("Sendung")} {c.trackingNumber}</span>}
              {c.number && <span className="block text-[11.5px] text-fg-subtle truncate">{c.number}</span>}
            </div>
            <div className="min-w-0 text-[12.5px] text-fg-muted">
              {c.note ? <p className="whitespace-pre-line">{c.note}</p> : <p className="text-fg-subtle">{t("Kein Hinweis")}</p>}
              {c.hasDocument && <p className="mt-1 text-[11.5px] text-fg-subtle flex items-center gap-1"><FileText className="size-3.5" /> {t("Fotos/Dokument beim Lager hinterlegt")}</p>}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Shopify-Stand einer Retoure – gleich in der Retouren-Ansicht und am Auftrag.
 * Logiful-Admins können eine noch nicht gebuchte Retoure (nach-)melden; Doppelschutz im Server.
 */
export function ShopifyReturnStatus({ caseId, shopify, canPush, invalidate }: {
  caseId: string;
  shopify: { state: ShopifyReturnState; message?: string | null; at?: string | null; refunded?: boolean } | null;
  canPush: boolean;
  invalidate: unknown[][];
}) {
  const { tenantId, me } = useTenant();
  const qc = useQueryClient();
  const push = useMutation({
    mutationFn: () => api.admin.pushReturnToShopify(tenantId, caseId),
    onSuccess: () => {
      toast.success("Wird an Shopify gemeldet", { description: msg("Das Ergebnis erscheint gleich hier.") });
      for (const k of invalidate) void qc.invalidateQueries({ queryKey: k });
    },
    onError: (e) => toast.error("Nicht gemeldet", { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "" }),
  });
  const showPush = me.user.staffRole === "staff_admin" && canPush && shopify?.state !== "done" && shopify?.state !== "pending";
  return (
    <div>
      <p className="flex flex-wrap items-center gap-2">
        {shopify ? <Badge tone={SHOPIFY_TONE[shopify.state]}>{SHOPIFY_LABEL[shopify.state]}</Badge> : <Badge>{t("Noch nicht an Shopify gemeldet")}</Badge>}
        {shopify?.refunded && <Badge tone="success">{t("Erstattet")}</Badge>}
        {shopify?.at && <span className="text-[12px] text-fg-subtle">{fmtDateTime(shopify.at)}</span>}
      </p>
      {shopify?.message && shopify.state !== "done" && <p className={cx("mt-1", shopify.state === "error" ? "text-danger" : "text-fg-muted")}>{serverText(shopify.message)}</p>}
      {showPush && (
        <button className="mt-1.5 text-[12px] text-fg-muted underline underline-offset-2 disabled:opacity-50" disabled={push.isPending} onClick={() => confirm("Diese Retoure jetzt an Shopify melden? In Shopify entsteht eine Retoure (ohne Erstattung).") && push.mutate()}>
          {t("Jetzt an Shopify melden")}
        </button>
      )}
    </div>
  );
}

const SHOPIFY_LABEL = { pending: "Wird an Shopify gemeldet", done: "In Shopify gebucht", skipped: "Nicht an Shopify gemeldet", error: "Meldung an Shopify fehlgeschlagen" } as const;
const SHOPIFY_SHORT = { pending: "wird gemeldet", done: "gebucht", skipped: "nicht gemeldet", error: "Fehler" } as const;
const SHOPIFY_TONE = { pending: "neutral", done: "success", skipped: "neutral", error: "danger" } as const;

function ReturnSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const { tenantId } = useTenant();
  // Shopify-Meldung läuft: nachladen, bis das Ergebnis da ist
  const q = useQuery({ queryKey: ["return", tenantId, id], queryFn: () => api.returns.get(tenantId, id), refetchInterval: (query) => (query.state.data?.shopify?.state === "pending" ? 3000 : false) });
  const r = q.data;
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content className="fixed z-50 right-0 top-0 h-full w-[min(560px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{t("Retoure")} {r?.shopOrderNumber ?? r?.warehouseOrderNumber ?? ""}</Dialog.Title>
              <Dialog.Description className="text-[12.5px] text-fg-muted">{r ? t("Geprüft {v}", { v: r.processedAt ? fmtDateTime(r.processedAt) : "–" }) : t("Wird geladen …")}</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          {!r ? (
            <div className="p-6"><Skeleton className="h-40 w-full" /></div>
          ) : (
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 text-[13px]">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={STATUS_TONE[r.status]}>{t(r.statusLabel)}</Badge>
                {r.undeliverable && <Badge tone="warning">{t("Rückläufer")}</Badge>}
                {r.order && <Link to={`/orders?open=${r.order.id}`} className="inline-flex items-center gap-1 font-medium underline underline-offset-2">{t("Auftrag")} {r.order.orderNumber} {t("öffnen")} <ExternalLink className="size-3" /></Link>}
                <span className="ml-auto"><ReturnsPortalButton variant="link" q={r.shopOrderNumber ?? r.order?.orderNumber ?? r.warehouseOrderNumber ?? undefined} /></span>
              </div>
              {r.customer && (
                <section>
                  <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Kunde")}</h3>
                  <p className="mt-1.5">{customerName(r)}{r.customer.company && r.customer.firstName ? ` · ${r.customer.company}` : ""}</p>
                  <p className="text-fg-muted">{[r.customer.zip, r.customer.city].filter(Boolean).join(" ")}{r.customer.country ? ` · ${countryName(r.customer.country)}` : ""}</p>
                  {r.customer.email && <p className="text-fg-muted">{r.customer.email}</p>}
                </section>
              )}
              <section>
                <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Artikel")}</h3>
                <ul className="mt-2 rounded-xl border border-border divide-y divide-border">
                  {r.lines.map((l) => (
                    <li key={l.id} className="px-3 py-2.5 flex items-start gap-3">
                      <span className="tabular-nums font-semibold w-7">{l.quantity}×</span>
                      <span className="min-w-0 flex-1">
                        {l.productId ? <Link to={`/products?open=${l.productId}`} className="font-medium hover:underline">{l.name ?? l.sku}</Link> : <span className="font-medium">{l.name ?? l.sku}</span>}
                        <span className="block font-mono text-[11.5px] text-fg-subtle">{l.sku}</span>
                        {l.undeliverableReason && <span className="block text-[12px] text-warning">{t("Rückläufer:")} {serverText(l.undeliverableReason)}</span>}
                        {!!l.conditionReasons?.length && <span className="block text-[12px] text-fg-muted">{t("Grund:")} {l.conditionReasons.join(", ")}</span>}
                      </span>
                      {l.condition && <Badge tone={CONDITION_TONE[l.condition] ?? "neutral"}>{CONDITION_LABEL[l.condition] ?? l.condition}</Badge>}
                    </li>
                  ))}
                </ul>
              </section>
              {(r.shopify || r.order?.shopify) && (
                <section>
                  <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Shopify")}</h3>
                  <div className="mt-1.5">
                    <ShopifyReturnStatus caseId={r.id} shopify={r.shopify} canPush={!!r.order?.shopify} invalidate={[["return", tenantId, r.id], ["returns", tenantId], ["order-returns", tenantId, r.order?.id ?? ""]]} />
                  </div>
                </section>
              )}
              {!!r.clarifications?.length && (
                <section>
                  <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Klärfall")}</h3>
                  <div className="mt-2"><ClarificationsList items={r.clarifications} loading={false} /></div>
                </section>
              )}
              {r.warehouseOrderNumber && <p className="text-[12px] text-fg-subtle">{t("Lager-Auftrag")} {r.warehouseOrderNumber}{r.notifiedAt ? t(" · gemeldet {v}", { v: fmtDateTime(r.notifiedAt) }) : ""}</p>}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
